import { Flex, useDisclosure, Box, Portal } from '@chakra-ui/react';
import { KModal, KText } from '@/ui-library/components';
import { useKFlags } from '@/hooks/use-k-flags';
import { COLORS } from '@/ui-library/design-system/constants/colors';
import type { AiScoringTypes } from '@/components/shared/ai-scoring/api/aiScoring.interface';
import { CenterSpinner } from '@/components/ats';

function calculatePercentageWeights(attrs) {
  const sorted = attrs.sort((a, b) => a.position - b.position);
  const n = sorted.length;

  if (n === 0) return attrs;

  // Calculate raw percentages
  const totalWeight = (n * (n + 1)) / 2;
  const percentages = sorted.map(
    (_, index) => ((n - index) / totalWeight) * 100
  );

  // Round and adjust to sum to 100%
  const rounded = percentages.map((p) => Math.round(p));
  const diff = 100 - rounded.reduce((sum, p) => sum + p, 0);

  // Distribute the difference starting from the largest values
  if (diff !== 0) {
    const indices = Array.from({ length: n }, (_, i) => i).sort(
      (a, b) => rounded[b] - rounded[a]
    ); // Sort by value desc

    for (let i = 0; i < Math.abs(diff); i++) {
      rounded[indices[i]] += diff > 0 ? 1 : -1;
    }
  }

  // Assign weights
  sorted.forEach((attr, index) => {
    attr.weight = rounded[index];
  });

  return attrs;
}

const PRIMARY_COLORS = [
  COLORS.primary[900],
  COLORS.primary[800],
  COLORS.primary[700],
  COLORS.primary[600],
  COLORS.primary[500],
  COLORS.primary[400],
  COLORS.primary[300],
  COLORS.primary[200],
  COLORS.primary[100],
];

// Find min and max weight in the data
const getMinMaxWeight = (attributes: AiScoringTypes.AiScoringAttribute[]) => {
  if (!attributes.length) return { min: 0, max: 1 };
  const weights = attributes.map((a) => a.weight);
  return { min: Math.min(...weights), max: Math.max(...weights) };
};

// Height interpolation: min 40px, max 64px
const getBarHeight = (weight: number, minWeight: number, maxWeight: number) => {
  const minH = 40;
  const maxH = 64;
  if (maxWeight === minWeight) {
    return maxH;
  }
  // Normalize between minWeight and maxWeight
  const barHeight =
    minH + ((weight - minWeight) / (maxWeight - minWeight)) * (maxH - minH);
  return barHeight;
};

export const AiScoreExplained = ({
  attributes,
  isLoading,
  disabled = false,
  useLocalFormula = false,
  onModalStateChange = (_isOpen) => {},
}: {
  attributes: AiScoringTypes.AiScoringAttribute[];
  isLoading?: boolean;
  disabled?: boolean;
  useLocalFormula?: boolean;
  onModalStateChange?: (_isOpen: boolean) => void;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { feJobTeammates } = useKFlags();

  const attrs = [...attributes].filter(
    (attr) => attr.description?.trim().length > 0
  );

  if (useLocalFormula) {
    calculatePercentageWeights(attrs);
  } else {
    attrs.sort((a, b) => b.weight - a.weight);
  }

  const { min, max } = getMinMaxWeight(attrs);

  const content = () => {
    return (
      <Flex
        w="100%"
        flexDir="column"
        alignItems="center"
        justifyContent="flex-start"
        bg={COLORS.secondary[25]}
        p="24px 0"
        gap="6px"
        maxH="80vh"
        overflowY="auto"
      >
        {/* 100% Bar */}
        <Flex w="100%">
          <Box
            w="106px"
            h="36px"
            mr="12px"
            bg={COLORS.primary[50]}
            borderRadius="6px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <KText
              variant="heading_4"
              color={COLORS.secondary[700]}
              fontWeight={700}
            >
              100%
            </KText>
          </Box>
        </Flex>
        {/* Attribute Rows */}
        <Flex w="100%" flexDir="column" gap="6px">
          {attrs.map((attr, idx) => {
            const colorIdx =
              idx < PRIMARY_COLORS.length ? idx : PRIMARY_COLORS.length - 1;
            const barColor = PRIMARY_COLORS[colorIdx] || COLORS.primary[950];
            const barHeight = getBarHeight(attr.weight, min, max);
            return (
              <Flex
                key={attr.label + idx}
                w="100%"
                maxW="500px"
                gap="10px"
                alignItems="center"
                justifyContent="center"
              >
                {/* Percentage Rectangle */}
                <Box
                  w="106px"
                  h={`${barHeight}px`}
                  bg={barColor}
                  borderRadius="6px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="sm"
                  transition="height 0.2s"
                >
                  <KText
                    variant="heading_4"
                    color={COLORS.base.white}
                    fontWeight={700}
                  >
                    {attr.weight} %
                  </KText>
                </Box>
                {/* Label */}
                <Flex flex="1" pr="18px">
                  <KText
                    variant="paragraph"
                    color={COLORS.primary[900]}
                    fontWeight={500}
                  >
                    {attr.description}
                  </KText>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <Flex
        gap="6px"
        alignItems="center"
        cursor={isLoading || disabled ? 'not-allowed' : 'pointer'}
        onClick={
          isLoading || disabled
            ? undefined
            : () => {
                onOpen();
                onModalStateChange(true);
              }
        }
        opacity={isLoading || disabled ? 0.5 : 1}
      >
        {/* <Icon as={PiQuestionDuotone as any} /> */}
        <KText variant="heading_4" color="primary-600">
          {feJobTeammates ? 'Scoring agent score breakdown' : 'Score breakdown'}
        </KText>
      </Flex>
      {isOpen && (
        <Portal containerRef={{ current: document.body }}>
          <KModal
            footer={false}
            isOpen={isOpen}
            onClose={() => {
              onClose();
              onModalStateChange(false);
            }}
            title={
              feJobTeammates
                ? 'Scoring agent score breakdown'
                : 'Score breakdown'
            }
            subHeaderText={
              feJobTeammates
                ? "Here's how the attributes are weighted by the Scoring agent"
                : "Here's how each attribute is weighted during the AI scoring process"
            }
            subHeaderTextProps={{
              fontWeight: '450',
            }}
            titleProps={{
              fontWeight: '650',
            }}
            modalContentProps={{
              w: '526px',
              maxW: '526px',
            }}
            modalBodyProps={{
              pr: '0px',
              bg: 'secondary-25',
              borderBottomRadius: '6px',
            }}
          >
            {isLoading ? (
              <Flex
                w="100%"
                h="100%"
                minH="400px"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <CenterSpinner
                  spinnerProps={{ color: 'primary-500' }}
                  h="100%"
                  w="100%"
                />
              </Flex>
            ) : (
              content()
            )}
          </KModal>
        </Portal>
      )}
    </>
  );
};
